import "./styles.css";

import brandIcon from "../assets/brand/MD - app icon - transparent.png";
import dungeonBackground from "../assets/brand/branding background - dungeon 1.png";
import heroCombat from "../assets/gameplay/raw screenshot 16by9 - 2.jpg";
import mediaLead from "../assets/gameplay/raw screenshot 16by9 - 9.jpg";
import tacticalCombat from "../assets/gameplay/raw screenshot 4by3 - 3.jpg";
import bossEncounter from "../assets/gameplay/raw screenshot 4by3 - 2.jpg";
import loadout from "../assets/gameplay/raw screenshot 4by3 - 4.jpg";
import enemyVariety from "../assets/gameplay/raw screenshot 4by3 - 7.jpg";
import darkExploration from "../assets/gameplay/raw screenshot 4by3 - 6.jpg";
import loadoutWide from "../assets/gameplay/raw screenshot 16by9 - 3.jpg";
import startScreen from "../assets/gameplay/raw screenshot 16by9 - 1.jpg";
import appBannerTransparent from "../assets/brand/MD - app banner - transparent - 2.png";
import appBannerBlack from "../assets/brand/MD - app banner - black background.png";
import pcBannerBlack from "../assets/brand/MD - pc banner - black background.png";
import appSplashHeader from "../assets/brand/MD - app splash header - transparent.png";
import textDivider from "../assets/brand/branding - text divider.png";
import ipadStoreOne from "../assets/gameplay/ipad store ss 1.png";
import ipadStoreTwo from "../assets/gameplay/ipad store ss 2.png";
import ipadStoreThree from "../assets/gameplay/ipad store ss 3.png";
import iphoneStoreOne from "../assets/gameplay/iphone store ss 1.png";
import iphoneStoreTwo from "../assets/gameplay/iphone store ss 2.png";
import iphoneStoreThree from "../assets/gameplay/iphone store ss 3.png";
import googlePlayBadge from "../assets/store/get-it-on-google-play.svg";
import appStoreBadge from "../assets/store/download-on-the-app-store.svg";

type ImageCard = {
  title: string;
  text: string;
  image: string;
  alt: string;
};

type StoreLink = {
  platform: string;
  label: string;
  url?: string;
  status: string;
  icon: string;
  badge?: string;
  badgeAlt?: string;
};

type BlogPost = {
  slug: string;
  title: string;
  date: string;
  category: string;
  author: string;
  excerpt: string;
  path: string;
  cardImage: string;
  cardImageAlt: string;
  heroImage?: string;
  heroImageAlt?: string;
  paragraphs: string[];
  bullets?: string[];
  media?: ImageCard[];
};

type ReleaseNote = {
  version: string;
  date: string;
  status: string;
  notes: string[];
};

type LegalSection = {
  title: string;
  body: string[];
  items?: string[];
};

type PressAsset = {
  title: string;
  description: string;
  image: string;
  alt: string;
  fileLabel: string;
};

type RouteTarget = {
  sectionId: string;
  title: string;
  page: "home" | "blog-post" | "press-kit" | "privacy" | "eula";
  post?: BlogPost;
};

type SeoMeta = {
  title: string;
  description: string;
  canonicalPath: string;
  type?: "website" | "article";
};

const siteOrigin = "https://www.muchdungeon.com";
const socialImageUrl = `${siteOrigin}/social-card.jpg`;
const googlePlayUrl = "https://play.google.com/store/apps/details?id=com.muchsoftware.android";
const appStoreUrl = "https://apps.apple.com/us/app/much-dungeon/id6758366489?uo=4";
const defaultDescription = "Much Dungeon is a dark fantasy top-down mobile roguelike dungeon crawler with procedural rooms, tactical combat, loot-driven loadouts, bosses, secrets, and 40+ enemies.";

const features: ImageCard[] = [
  {
    title: "Tactical Combat",
    text: "Manage health, mana, energy, positioning, and combat logs as enemies close in from the dark.",
    image: tacticalCombat,
    alt: "Top-down dungeon combat in a dark stone room with the player fighting several Nibblen enemies."
  },
  {
    title: "Boss Encounters",
    text: "Face 7+ dungeon bosses where timing, gear, positioning, and status effects matter.",
    image: bossEncounter,
    alt: "Close crop of a boss fight with damage numbers and tactical grid highlights."
  },
  {
    title: "Loot And Loadouts",
    text: "Build around 36+ weapons, 17+ ranged weapons, 12+ wands, potions, scrolls, curses, and enchantments.",
    image: loadout,
    alt: "Gear screen showing equipment slots, inventory items, scrolls, potions, and a player character."
  },
  {
    title: "Enemy Variety",
    text: "Learn 40+ enemy types, elite and minion variants, monster swarms, and NPC infighting.",
    image: enemyVariety,
    alt: "Dungeon hallway encounter with several enemy types, targeting arrows, attack effects, and combat log."
  },
  {
    title: "Procedural Dungeons",
    text: "Explore generated levels, rooms, encounters, 7+ biomes, secrets, traps, puzzles, altars, and spreading fire.",
    image: darkExploration,
    alt: "Player character exploring a dark dungeon room with torches, enemies, and combat UI."
  }
];

const mediaItems: ImageCard[] = [
  {
    title: "Loadout Choices",
    text: "Weapons with special actions, armor, wands, scrolls, potions, and item slots shape each run.",
    image: loadoutWide,
    alt: "Player loadout screen with equipment and inventory items."
  },
  {
    title: "Close Combat",
    text: "Low health, critical hits, and skeleton minions closing in turn boss fights into knife-edge decisions.",
    image: bossEncounter,
    alt: "Boss fight where the player is critically wounded, with low-life red corner overlays, damage numbers, critical hit callouts, and skeleton minions swarming in."
  },
  {
    title: "Store Promo",
    text: "Promotional screenshots can support press and store sections.",
    image: startScreen,
    alt: "Much Dungeon start screen with the title, menu options, and character holding a sword."
  }
];

const storeLinks: StoreLink[] = [
  {
    platform: "Google Play",
    label: "Get it on Google Play",
    url: googlePlayUrl,
    status: "Production link",
    icon: "G",
    badge: googlePlayBadge,
    badgeAlt: "Get it on Google Play"
  },
  {
    platform: "App Store",
    label: "Download on the App Store",
    url: appStoreUrl,
    status: "Production link",
    icon: "A",
    badge: appStoreBadge,
    badgeAlt: "Download on the App Store"
  }
];

const blogPosts: BlogPost[] = [
  {
    slug: "imminent-release-and-new-site",
    title: "Much Dungeon is nearing release, and the site has a new home",
    date: "2026-06-29",
    category: "Announcement",
    author: "Much Software",
    excerpt: "Much Dungeon is nearing release, and the refreshed website makes it easier to find gameplay details, launch links, updates, and official policies.",
    path: "/post/imminent-release-and-new-site",
    cardImage: startScreen,
    cardImageAlt: "Much Dungeon start screen with the title, menu options, and character holding a sword.",
    heroImage: heroCombat,
    heroImageAlt: "Top-down dungeon combat against a boss with enemies, ability buttons, and highlighted attack tiles.",
    paragraphs: [
      "Much Dungeon is getting close to release. The Android listing is already live for beta testing, the App Store path is moving through final approval, and the refreshed website is ready to support launch.",
      "The new site puts the game first: clearer screenshots, stronger store cards, a better look at combat and loadouts, and a simpler way to find official updates.",
      "We also added dedicated pages for release notes, the End User License Agreement, and the Privacy Policy so players can find the latest public information in one place.",
      "This blog will be used for launch news, update highlights, gameplay notes, and behind-the-scenes posts that are useful to players and creators."
    ],
    bullets: [
      "Google Play and App Store sections are ready for launch links.",
      "Release notes now have a dedicated page for version updates.",
      "Privacy Policy and EULA pages are available from the footer.",
      "The media section now focuses on actual gameplay screenshots."
    ],
    media: [
      {
        title: "Combat preview",
        text: "Boss fights, low-health alerts, and crowded rooms are part of the core game signal.",
        image: bossEncounter,
        alt: "Boss fight where the player is critically wounded with damage callouts and skeleton minions nearby."
      },
      {
        title: "Loadout decisions",
        text: "Equipment, scrolls, potions, and item slots shape each run.",
        image: loadout,
        alt: "Gear screen showing equipment slots, inventory items, scrolls, potions, and a player character."
      }
    ]
  }
];

const releaseNotes: ReleaseNote[] = [
  {
    version: "1.0.229",
    date: "2026-06-29",
    status: "First release build",
    notes: [
      "First release build.",
      "Welcome to Much Dungeon!"
    ]
  },
  {
    version: "Website 0.2.0",
    date: "2026-06-29",
    status: "Site refresh",
    notes: [
      "Refreshed the public website with clearer gameplay media, store links, and launch information.",
      "Added dedicated pages for blog posts, release notes, the EULA, and the Privacy Policy."
    ]
  }
];

const pressFacts = [
  ["Game", "Much Dungeon"],
  ["Developer", "Much Software, LLC"],
  ["Genre", "Mobile top-down roguelike dungeon crawl"],
  ["Platforms", "iOS and Android"],
  ["Status", "Final release approval"],
  ["Style", "Dark fantasy, cell-shaded/vector-like art"],
  ["Dungeon generation", "Procedurally generated levels, rooms, and encounters"],
  ["Content scope", "7+ biomes, 7+ bosses, 40+ unique enemies"],
  ["Enemy systems", "Elite and minion variants, NPC infighting, and unique AI"],
  ["Items", "36+ weapons with special actions, 17+ ranged weapons, 12+ wands, 23+ potions, 14+ scrolls"],
  ["Dungeon systems", "Dynamic lighting, destructible environments, fire propagation, secrets, traps, puzzles, and altars"],
  ["Progression", "Player leveling, item leveling, character effects, enchantments, curses, leaderboards, and cosmetic skins"],
  ["Logo font", "EvilEmpire"],
  ["Website", "muchdungeon.com"],
  ["Contact", "support@muchsoftware.com"]
];

const pressLogoAssets: PressAsset[] = [
  {
    title: "Transparent app icon",
    description: "Primary icon for app, press, and creator usage.",
    image: brandIcon,
    alt: "Much Dungeon stone arch icon with stairs, skull, and bones.",
    fileLabel: "PNG"
  },
  {
    title: "Transparent app banner",
    description: "Logo treatment for transparent-background placements.",
    image: appBannerTransparent,
    alt: "Much Dungeon app banner logo on a transparent background.",
    fileLabel: "PNG"
  },
  {
    title: "Black-background app banner",
    description: "Logo treatment with built-in dark background.",
    image: appBannerBlack,
    alt: "Much Dungeon app banner logo on a black background.",
    fileLabel: "PNG"
  },
  {
    title: "PC banner",
    description: "Wider Much Dungeon banner for desktop or article layouts.",
    image: pcBannerBlack,
    alt: "Much Dungeon PC banner logo on a black background.",
    fileLabel: "PNG"
  },
  {
    title: "Splash header",
    description: "Transparent splash header art for launch coverage.",
    image: appSplashHeader,
    alt: "Much Dungeon splash header logo artwork.",
    fileLabel: "PNG"
  },
  {
    title: "Text divider",
    description: "Decorative divider pulled from the current brand set.",
    image: textDivider,
    alt: "Much Dungeon decorative text divider.",
    fileLabel: "PNG"
  }
];

const pressPromoAssets: PressAsset[] = [
  {
    title: "iPad store screenshot 1",
    description: "Promotional tablet screenshot with store-ready copy.",
    image: ipadStoreOne,
    alt: "Much Dungeon iPad promotional screenshot with launch copy.",
    fileLabel: "PNG"
  },
  {
    title: "iPad store screenshot 2",
    description: "Promotional tablet screenshot with gameplay and copy.",
    image: ipadStoreTwo,
    alt: "Much Dungeon iPad promotional screenshot with gameplay.",
    fileLabel: "PNG"
  },
  {
    title: "iPad store screenshot 3",
    description: "Promotional tablet screenshot for press and store coverage.",
    image: ipadStoreThree,
    alt: "Much Dungeon iPad promotional screenshot.",
    fileLabel: "PNG"
  },
  {
    title: "iPhone store screenshot 1",
    description: "Promotional phone screenshot with copy and logo treatment.",
    image: iphoneStoreOne,
    alt: "Much Dungeon iPhone promotional screenshot with copy.",
    fileLabel: "PNG"
  },
  {
    title: "iPhone store screenshot 2",
    description: "Promotional phone screenshot for mobile-focused coverage.",
    image: iphoneStoreTwo,
    alt: "Much Dungeon iPhone promotional screenshot.",
    fileLabel: "PNG"
  },
  {
    title: "iPhone store screenshot 3",
    description: "Promotional phone screenshot with gameplay framing.",
    image: iphoneStoreThree,
    alt: "Much Dungeon iPhone promotional screenshot with gameplay framing.",
    fileLabel: "PNG"
  }
];

const pressGameplayAssets: PressAsset[] = [
  {
    title: "Boss combat",
    description: "Boss encounter with low-health alert, damage callouts, and minions.",
    image: bossEncounter,
    alt: "Boss fight where the player is critically wounded with damage callouts and skeleton minions nearby.",
    fileLabel: "JPG"
  },
  {
    title: "Tactical combat",
    description: "Top-down tactical combat in a dark dungeon room.",
    image: tacticalCombat,
    alt: "Top-down dungeon combat in a dark stone room.",
    fileLabel: "JPG"
  },
  {
    title: "Loadout screen",
    description: "Equipment, inventory, potions, and scrolls.",
    image: loadout,
    alt: "Gear screen showing equipment slots, inventory items, scrolls, potions, and a player character.",
    fileLabel: "JPG"
  },
  {
    title: "Enemy variety",
    description: "Encounter scene showing enemies, attacks, targeting, and UI.",
    image: enemyVariety,
    alt: "Dungeon hallway encounter with several enemy types and targeting arrows.",
    fileLabel: "JPG"
  },
  {
    title: "Dark exploration",
    description: "Dungeon exploration with torches, enemies, and hostile rooms.",
    image: darkExploration,
    alt: "Player character exploring a dark dungeon room with torches and enemies.",
    fileLabel: "JPG"
  }
];

const eulaSections: LegalSection[] = [
  {
    title: "Introduction",
    body: [
      "This End User License Agreement (\"Agreement\") explains the terms that apply to your use of our mobile game on iOS and Android devices.",
      "We believe legal documents should be easy to understand, so we have written this Agreement in plain language. By tapping \"Accept & Continue,\" downloading, installing, or using the game, you acknowledge that you have read, understood, and agree to be bound by this Agreement and our Privacy Policy. If you do not agree to these terms, do not install, access, or use the game.",
      "You may review this Agreement and our Privacy Policy at any time within the game's Settings menu or on our website."
    ]
  },
  {
    title: "At a Glance: Summary of Key Points",
    body: [],
    items: [
      "You are borrowing, not buying: We give you a personal, non-commercial license to play our game on your mobile devices. You do not own the software itself.",
      "Play fair: We ask that you do not hack, reverse engineer, or modify the game. This keeps the experience fun and fair for all players.",
      "In-app purchases are licenses: When you buy virtual items or currency, you are purchasing a limited license to use them within the game, not acquiring real-world property.",
      "We protect your data: We collect limited technical and operational data to help operate and improve the game, and we handle personal information in accordance with applicable privacy laws.",
      "The game is \"as is\": While we work hard to provide a great experience, we cannot guarantee the game will be completely free of bugs or server interruptions."
    ]
  },
  {
    title: "1. License Grant",
    body: [
      "When you download our game from the Apple App Store or Google Play Store, we are granting you a specific set of permissions to use our software. Much Software, LLC, and not Apple or Google, is solely responsible for the game and its content.",
      "What You Are Getting",
      "Much Software, LLC grants you a personal, limited, non-exclusive, non-transferable, and revocable license to install and play our game. This means the game is for your personal entertainment only. You may not use the game for any commercial purposes, such as operating a public arcade or charging others to play.",
      "Scope of Use and Installations",
      "You may download and install the game on any compatible iOS or Android device that you own or legally control, subject to the terms of this Agreement and any applicable platform rules.",
      "If you downloaded the game through the Apple App Store, Much Software, LLC grants you a personal, limited, non-exclusive, non-transferable, and revocable license to use the game on any Apple-branded products that you own or control, and as permitted by the Usage Rules contained in the Apple Media Services Terms and Conditions. Except as permitted by those Usage Rules, you may not distribute or make the game available over a network where it could be used by multiple devices at the same time. You also acknowledge that the game may be accessed and used by other accounts associated with the purchaser through Family Sharing or volume purchasing, where applicable.",
      "If you downloaded the game through Google Play, your use of the game must comply with applicable Google Play terms, policies, and rules. In the event of a conflict between those platform rules and this Agreement, the applicable platform rules will control solely to the extent required by law or platform policy.",
      "Because our game features in-app purchases, any virtual goods, virtual currency, or premium features you acquire are also licensed to you under these same personal, non-commercial terms."
    ]
  },
  {
    title: "1.1 Eligibility and Minors",
    body: [
      "You must be at least thirteen (13) years old, or the minimum age required by applicable law in your jurisdiction, to use the game without parental supervision.",
      "If you are under the age of majority where you live, you may only use the game with the involvement and consent of a parent or legal guardian. By downloading, installing, or using the game, you represent that you meet these requirements or have obtained the necessary parental or guardian consent.",
      "Parents and guardians are responsible for supervising the use of the game by minors and for any purchases or activities conducted through their accounts or devices."
    ]
  },
  {
    title: "2. Restrictions",
    body: [
      "To ensure a secure, fair, and enjoyable environment for our global community of players, we have to set some ground rules about what you cannot do with our software.",
      "Clear Limitations on Use",
      "By using our game, you agree that you will not:"
    ],
    items: [
      "Reverse Engineer or Decompile: You may not take the software apart, decompile it, or attempt to extract the source code. We enforce this to protect our intellectual property and prevent the creation of cheats.",
      "Redistribute or Sell: You may not sell, rent, lease, or distribute copies of the game, your account, or any in-app purchases to others.",
      "Sublicense: You cannot transfer your license to play the game to another person.",
      "Modify or Hack: You may not modify the game files, create unauthorized derivative works, or use third-party software to gain an unfair advantage (cheating)."
    ]
  },
  {
    title: "3. Ownership",
    body: [
      "It is important to clarify who owns what when you interact with our game.",
      "Our Intellectual Property",
      "Much Software, LLC retains all rights, title, and interest in and to the game. This includes all computer code, artwork, animations, sounds, music, storylines, and virtual items available through in-app purchases. Downloading the game does not transfer any ownership rights to you.",
      "Your Data and Feedback",
      "You retain all ownership rights to your personal data. However, if you send us feedback, suggestions, or ideas on how to improve the game, you grant Much Software, LLC a perpetual, worldwide, royalty-free license to use those ideas without any obligation to compensate you. We love hearing from our players, and this allows us to freely implement community suggestions to make the game better."
    ]
  },
  {
    title: "4. Updates & Support",
    body: [
      "Mobile technology changes rapidly, and we continuously work to improve our game.",
      "Update Policies",
      "We may automatically deploy updates, patches, or modifications to the game to add new features, fix bugs, or balance gameplay. Sometimes, these updates are mandatory to continue playing. We reserve the right to modify, rebalance, add, remove, or discontinue gameplay features, virtual items, rewards, progression systems, and other game content at any time.",
      "Support Availability",
      "While we strive to provide a seamless gaming experience, we do not guarantee 24/7 technical support. If you encounter issues, you can reach out to us at support@muchsoftware.com, and our team will do our best to assist you during normal business hours. Please note that older devices or outdated operating systems may eventually lose compatibility with the game as we push new updates.",
      "Much Software, LLC, and not Apple, is solely responsible for providing any maintenance and support services for the game, to the extent required by this Agreement or applicable law."
    ]
  },
  {
    title: "5. Privacy & Data",
    body: [
      "We collect limited technical and operational information necessary to operate and improve the game. This may include crash reports, gameplay analytics, device information, and in-app purchase transaction information provided through platform services such as Apple App Store and Google Play.",
      "In-app purchases are processed directly by Apple or Google through their respective payment systems. Much Software, LLC does not directly collect or store payment card information.",
      "We use trusted third-party service providers, including Google Firebase services such as Analytics and Crashlytics, to help us understand game performance, diagnose crashes, and improve the player experience.",
      "We do not sell personal information to third parties.",
      "We handle personal information in accordance with applicable privacy laws. For additional details about what information may be collected and how it is used, please review our Privacy Policy at https://www.muchdungeon.com/privacy-policy."
    ]
  },
  {
    title: "6. Termination",
    body: [
      "This Agreement remains in effect until terminated by either you or Much Software, LLC.",
      "How Termination Happens",
      "You can terminate this Agreement at any time simply by deleting the game from all your devices. We may automatically terminate this Agreement and suspend or ban your account if you violate any of the restrictions outlined in Section 2 (such as cheating or hacking) at our reasonable discretion.",
      "Effect of Termination",
      "If this Agreement is terminated, your license to use the game is immediately revoked. You may lose access to the game, your account progress, and any virtual items, virtual currency, or premium features associated with your account.",
      "To the maximum extent permitted by applicable law, virtual items, virtual currency, and premium features have no monetary value, are non-transferable, and may be suspended, revoked, or forfeited if your account is terminated due to a violation of this Agreement.",
      "We do not provide refunds for virtual items, virtual currency, premium features, or other in-app purchases solely because an account has been suspended or terminated for violating this Agreement.",
      "Upon termination, we will retain or delete personal information in accordance with our Privacy Policy and applicable law."
    ]
  },
  {
    title: "7. Warranties & Disclaimers",
    body: [
      "We want to be completely honest about the limitations of mobile software.",
      "What Is and Isn't Guaranteed",
      "We provide the game on an \"as is\" and \"as available\" basis. While we put a lot of love and effort into our software, we do not warrant that the game will be entirely free of bugs, crashes, or server downtimes. We disclaim all warranties, whether express or implied, including implied warranties of merchantability or fitness for a particular purpose.",
      "To the extent any warranty cannot be effectively disclaimed under applicable law, Much Software, LLC, and not Apple, is solely responsible for that warranty and for any claims, losses, liabilities, damages, costs, or expenses attributable to any failure of the game to conform to that warranty.",
      "Limitation of Liability",
      "To the maximum extent permitted by law, Much Software, LLC shall not be liable for any indirect, incidental, special, or consequential damages arising out of your use of or inability to use the game. This includes loss of data, loss of in-app purchases due to device failure, or any other intangible losses. In no event shall our total liability to you exceed the greater of (a) the amount you paid to us through in-app purchases during the twelve (12) months preceding the claim, or (b) one hundred U.S. dollars ($100)."
    ]
  },
  {
    title: "8. Governing Law",
    body: [
      "This Agreement shall be governed by and construed in accordance with the laws of the State of Georgia, United States, without regard to its conflict of law principles.",
      "If you reside outside the United States, including in the European Union, the United Kingdom, or other jurisdictions with mandatory consumer protection laws, nothing in this Agreement limits or deprives you of any rights or protections that cannot legally be waived under the laws of your place of residence.",
      "To the extent permitted by applicable law, any disputes arising out of or relating to this Agreement or your use of the game shall be resolved exclusively in the state or federal courts located in the State of Georgia, and you consent to the personal jurisdiction of those courts."
    ]
  },
  {
    title: "9. Mobile Platform Terms",
    body: [
      "You must also comply with any applicable third-party terms of agreement when using the game and its related services.",
      "Apple App Store Additional Terms",
      "If you download the game from Apple's App Store, you acknowledge and agree that:"
    ],
    items: [
      "This Agreement is between you and Much Software, LLC, and not with Apple Inc.",
      "Apple is not responsible for the game or its content.",
      "Apple has no obligation to provide maintenance or support services for the game.",
      "To the maximum extent permitted by applicable law, Apple has no warranty obligations with respect to the game.",
      "In the event of any failure of the game to conform to an applicable warranty, you may notify Apple, and Apple may refund the purchase price for the game, if any, in accordance with Apple's applicable policies. To the maximum extent permitted by law, Apple will have no other warranty obligation whatsoever with respect to the game.",
      "Much Software, LLC, and not Apple, is responsible for addressing any claims relating to the game or your possession and use of the game, including product liability claims, claims that the game fails to conform to any applicable legal or regulatory requirement, and claims arising under consumer protection, privacy, or similar laws.",
      "In the event of any third-party claim that the game or your possession and use of the game infringes that third party's intellectual property rights, Much Software, LLC, and not Apple, will be solely responsible for the investigation, defense, settlement, and discharge of that intellectual property infringement claim.",
      "Apple and its subsidiaries are third-party beneficiaries of this Agreement and may enforce this Agreement against you."
    ]
  },
  {
    title: "Google Play Additional Terms",
    body: ["If you download the game from Google Play, your use of the game is also subject to applicable Google Play terms and policies."]
  },
  {
    title: "Export Compliance",
    body: ["You represent and warrant that you are not located in a country or region subject to a U.S. government embargo, or designated by the U.S. government as a terrorist-supporting country, and that you are not listed on any U.S. government list of prohibited or restricted parties. You agree not to use, export, or re-export the game in violation of applicable U.S. export control or economic sanctions laws."]
  },
  {
    title: "Contact",
    body: [
      "If you have any questions, complaints, or claims about this End User License Agreement or the Licensed Application, please contact Much Software, LLC at:",
      "Much Software, LLC\nPO Box 5686\nAthens, GA 30604",
      "Phone: +1 706-363-0607\nEmail: support@muchsoftware.com"
    ]
  }
];

const privacySections: LegalSection[] = [
  {
    title: "Introduction",
    body: [
      "Much Software, LLC (\"Much Software,\" \"we,\" \"our,\" or \"us\") respects your privacy. This Privacy Policy explains how we collect, use, and protect information when you use the Much Dungeon mobile game, visit our website, or otherwise interact with our services (collectively, the \"Services\").",
      "By downloading, installing, or using the Services, you agree to the practices described in this Privacy Policy."
    ]
  },
  {
    title: "Information We Collect",
    body: ["When you use our Services, certain information may be collected automatically by us or our service providers, including:"],
    items: [
      "Device type and operating system",
      "App or software version",
      "Gameplay and usage statistics",
      "Crash reports and diagnostic information",
      "Device identifiers and application instance identifiers used for analytics, crash reporting, and service functionality",
      "General geographic region derived from IP address",
      "Performance and analytics information"
    ]
  },
  {
    title: "Website Information",
    body: [
      "When you visit our website, basic technical information such as browser type, device type, IP address, and website usage information may also be collected by Wix or our service providers to operate and secure the website.",
      "This information is used to operate, maintain, improve, and troubleshoot our Services."
    ]
  },
  {
    title: "Leaderboards and Achievements",
    body: [
      "If you use Apple Game Center or Google Play Games Services, leaderboard information, achievements, and player identifiers may be processed through those platforms to provide game functionality.",
      "We do not create or maintain separate user accounts for Much Dungeon."
    ]
  },
  {
    title: "Information You Provide",
    body: ["If you contact us by email or through our website, we may receive information you choose to provide, such as your name, email address, and the contents of your message."]
  },
  {
    title: "In-App Purchases",
    body: [
      "The Game may offer optional in-app purchases.",
      "All purchases are processed by Apple App Store or Google Play Store. Much Software does not collect, receive, or store payment card information or other payment details associated with purchases.",
      "Purchases are subject to the privacy policies and terms of the applicable app store provider."
    ]
  },
  {
    title: "Third-Party Services",
    body: ["Our Services may use the following third-party providers:"],
    items: [
      "Firebase Analytics (Google)",
      "Firebase Crashlytics (Google)",
      "Apple Game Center",
      "Google Play Games Services",
      "Apple App Store",
      "Google Play Store"
    ]
  },
  {
    title: "International Data Transfers",
    body: ["Information collected through our Services may be processed and stored in the United States or other countries where our service providers operate. By using the Services, you understand that information may be transferred to and processed in countries that may have different data protection laws than your country of residence."]
  },
  {
    title: "How We Use Information",
    body: ["We use collected information to:"],
    items: [
      "Operate and maintain our Services",
      "Improve gameplay and user experience",
      "Monitor performance and stability",
      "Diagnose and fix crashes and technical issues",
      "Maintain leaderboards and achievements",
      "Respond to support requests and user inquiries",
      "Protect the security and integrity of our Services"
    ]
  },
  {
    title: "Data Sharing",
    body: [
      "We do not sell personal information and do not share personal information for cross-context behavioral advertising.",
      "We may share information with service providers that assist us with analytics, crash reporting, leaderboard functionality, payment processing, hosting, or other operational functions necessary to provide our Services.",
      "We may also disclose information if required by law or to protect our legal rights."
    ]
  },
  {
    title: "Data Retention",
    body: [
      "We retain information only as long as reasonably necessary to provide and improve our Services, comply with legal obligations, resolve disputes, and enforce our agreements.",
      "Information collected by third-party providers is subject to their respective retention policies."
    ]
  },
  {
    title: "Children's Privacy",
    body: [
      "Our Services are not directed to children under the age of 13, and we do not knowingly collect personal information from children under 13.",
      "If you believe that a child has provided personal information to us, please contact us and we will take reasonable steps to remove the information.",
      "Parents and guardians are encouraged to monitor children's online activity."
    ]
  },
  {
    title: "Cookies and Similar Technologies",
    body: [
      "Our website may use cookies and similar technologies provided by Wix and other service providers to operate, secure, and improve the website. These technologies may be used to remember preferences, maintain website functionality, analyze website usage, and protect against fraud or abuse.",
      "You can control cookies through your browser settings. Certain website features may not function properly if cookies are disabled."
    ]
  },
  {
    title: "Data Security",
    body: [
      "We take reasonable administrative, technical, and organizational measures to protect information processed through our Services.",
      "However, no method of electronic transmission or storage is completely secure, and we cannot guarantee absolute security."
    ]
  },
  {
    title: "Your Privacy Rights",
    body: ["Depending on your location and applicable law, you may have rights regarding personal information about you, including rights to access, correct, delete, or restrict certain processing activities.", "To make a privacy-related request, please contact us at support@muchsoftware.com."]
  },
  {
    title: "Changes to This Privacy Policy",
    body: [
      "We may update this Privacy Policy from time to time.",
      "Updated versions will be posted on our website, within the Game, or through the applicable app store listing. The revised version will become effective when posted."
    ]
  },
  {
    title: "Contact Us",
    body: [
      "If you have questions about this Privacy Policy, please contact:",
      "Much Software, LLC\nPO Box 5686\nAthens, GA 30604",
      "Phone: +1 706-363-0607\nEmail: support@muchsoftware.com"
    ]
  }
];

const routeTargets: Record<string, RouteTarget> = {
  "/": { sectionId: "top", title: "Much Dungeon", page: "home" },
  "/media": { sectionId: "media", title: "Media | Much Dungeon", page: "home" },
  "/blog-1": { sectionId: "blog", title: "Blog | Much Dungeon", page: "home" },
  "/release-notes": { sectionId: "updates", title: "Release Notes | Much Dungeon", page: "home" },
  "/press-kit": { sectionId: "press-kit-page", title: "Press Kit | Much Dungeon", page: "press-kit" },
  "/privacy-policy": { sectionId: "privacy-policy-page", title: "Privacy Policy | Much Dungeon", page: "privacy" },
  "/eula": { sectionId: "eula-page", title: "EULA | Much Dungeon", page: "eula" }
};

const routeMeta: Record<string, SeoMeta> = {
  "/": {
    title: "Much Dungeon",
    description: defaultDescription,
    canonicalPath: "/"
  },
  "/media": {
    title: "Screenshots and Gameplay | Much Dungeon",
    description: "View Much Dungeon gameplay screenshots, boss fights, loadouts, procedural rooms, enemies, lighting, and dungeon exploration.",
    canonicalPath: "/media"
  },
  "/blog-1": {
    title: "Blog | Much Dungeon",
    description: "Read Much Dungeon announcements, launch news, gameplay notes, release highlights, and development updates.",
    canonicalPath: "/blog-1"
  },
  "/release-notes": {
    title: "Release Notes | Much Dungeon",
    description: "Read the latest Much Dungeon release notes, version updates, launch information, fixes, and public changelog entries.",
    canonicalPath: "/release-notes"
  },
  "/press-kit": {
    title: "Press Kit | Much Dungeon",
    description: "Download Much Dungeon logos, app icons, promotional screenshots, gameplay screenshots, factsheet details, and brand usage notes.",
    canonicalPath: "/press-kit"
  },
  "/privacy-policy": {
    title: "Privacy Policy | Much Dungeon",
    description: "Read the Much Dungeon Privacy Policy for information about analytics, crash reporting, leaderboards, support requests, and data practices.",
    canonicalPath: "/privacy-policy"
  },
  "/eula": {
    title: "EULA | Much Dungeon",
    description: "Read the Much Dungeon End User License Agreement for game licensing, restrictions, ownership, support, platform terms, and legal notices.",
    canonicalPath: "/eula"
  }
};

const app = document.querySelector<HTMLDivElement>("#app");

if (!app) {
  throw new Error("Missing #app root");
}

document.documentElement.style.setProperty("--hero-bg", `url("${dungeonBackground}")`);

app.innerHTML = `
  <header class="site-header" data-header>
    <a class="brand-link" href="#top" aria-label="Much Dungeon home">
      <img src="${brandIcon}" alt="" aria-hidden="true" class="brand-icon">
      <span>Much Dungeon</span>
    </a>
    <button class="menu-button" type="button" aria-expanded="false" aria-controls="site-nav" data-menu-button>
      <span class="menu-button__bar"></span>
      <span class="menu-button__bar"></span>
      <span class="menu-button__bar"></span>
      <span class="sr-only">Menu</span>
    </button>
    <nav class="site-nav" id="site-nav" data-site-nav>
      <a href="/media" data-route-link>Media</a>
      <a href="/blog-1" data-route-link>Blog</a>
      <a href="/release-notes" data-route-link>Release Notes</a>
    </nav>
    <a class="header-cta" href="#stores">Get The Game</a>
  </header>

  <main id="top" data-page="home">
    <section class="hero-section" aria-labelledby="hero-title">
      <div class="hero-bg" aria-hidden="true"></div>
      <div class="section-shell hero-shell">
        <div class="hero-copy">
          <p class="eyebrow">Mobile roguelike dungeon crawl</p>
          <h1 id="hero-title">Much Dungeon</h1>
          <p class="hero-lede">
            Strike, loot, and adapt through dark fantasy rooms packed with tactical fights,
            strange enemies, and build-defining gear.
          </p>
          <div class="hero-actions" aria-label="Primary actions">
            <a class="button button-primary" href="#stores">Get The Game</a>
            <a class="button button-secondary" href="#media">View Gameplay</a>
          </div>
        </div>

        <figure class="hero-media">
          <div class="game-frame">
            <img src="${heroCombat}" alt="Top-down dungeon combat against a boss with enemies, ability buttons, and highlighted attack tiles.">
            <button class="frame-trigger image-trigger" type="button" data-lightbox-src="${heroCombat}" data-lightbox-alt="Top-down dungeon combat against a boss with enemies, ability buttons, and highlighted attack tiles." data-lightbox-caption="Tactical combat against Queen Skjeks">
              <span class="view-full">View full image</span>
            </button>
          </div>
          <figcaption>Tactical combat against Queen Skjeks</figcaption>
        </figure>
      </div>
    </section>

    <section class="store-section" id="stores" aria-labelledby="stores-title">
      <div class="section-shell store-shell">
        <div class="section-heading store-heading">
          <p class="eyebrow">Launch platforms</p>
          <h2 id="stores-title">Get Much Dungeon</h2>
        </div>
        <div class="store-grid" data-store-grid></div>
      </div>
    </section>

    <section class="feature-section" aria-labelledby="features-title">
      <div class="section-shell">
        <div class="section-heading">
          <p class="eyebrow">What waits below</p>
          <h2 id="features-title">Fight, loot, survive</h2>
        </div>
        <div class="feature-grid" data-feature-grid></div>
      </div>
    </section>

    <section class="media-section" id="media" aria-labelledby="media-title">
      <div class="section-shell media-shell">
        <div class="section-heading">
          <p class="eyebrow">Gameplay</p>
          <h2 id="media-title">Rooms worth entering carefully</h2>
        </div>
        <div class="media-layout">
          <figure class="media-lead">
            <button class="media-image-button image-trigger" type="button" data-lightbox-src="${mediaLead}" data-lightbox-alt="A bright cave combat scene with multiple monsters, damage numbers, loot, and combat UI." data-lightbox-caption="Enemy variety, shifting light, and crowded encounters.">
              <img src="${mediaLead}" alt="A bright cave combat scene with multiple monsters, damage numbers, loot, and combat UI.">
              <span class="view-full">View full image</span>
            </button>
            <figcaption>Enemy variety, shifting light, and crowded encounters.</figcaption>
          </figure>
          <div class="media-stack" data-media-stack></div>
        </div>
      </div>
    </section>

    <section class="blog-section" id="blog" aria-labelledby="blog-title">
      <div class="section-shell">
        <div class="section-heading">
          <p class="eyebrow">Dev logs</p>
          <h2 id="blog-title">From the dungeon desk</h2>
        </div>
        <div class="blog-grid" data-blog-grid></div>
      </div>
    </section>

    <section class="updates-section" id="updates" aria-labelledby="updates-title">
      <div class="section-shell">
        <div class="section-heading">
          <p class="eyebrow">Release notes</p>
          <h2 id="updates-title">What changed</h2>
        </div>
        <div class="release-list" data-release-list></div>
      </div>
    </section>
  </main>

  <main class="post-page" id="blog-post-page" data-page="blog-post" hidden>
    <article class="section-shell post-document">
      <a class="back-link" href="/blog-1" data-route-link>Back to Blog</a>
      <div data-post-detail></div>
    </article>
  </main>

  <main class="press-kit-page" id="press-kit-page" data-page="press-kit" hidden>
    <section class="press-kit-section" aria-labelledby="press-kit-title">
      <div class="section-shell press-kit-shell">
        <a class="back-link" href="/" data-route-link>Back to Much Dungeon</a>
        <p class="eyebrow">Press and creators</p>
        <h1 id="press-kit-title">Much Dungeon Press Kit</h1>
        <p class="section-intro">
          Approved public materials for launch coverage, creator thumbnails,
          articles, and store-adjacent promotion.
        </p>

        <section class="press-kit-block" aria-labelledby="factsheet-title">
          <h2 id="factsheet-title">Factsheet</h2>
          <dl class="factsheet" data-press-facts></dl>
        </section>

        <section class="press-kit-block" aria-labelledby="boilerplate-title">
          <h2 id="boilerplate-title">Short Description</h2>
          <p>
            Much Dungeon is a mobile top-down roguelike dungeon crawl with
            procedurally generated levels, tactical combat, loot-driven loadouts,
            destructible rooms, dynamic lighting, boss encounters, and a dark
            fantasy cell-shaded art style.
          </p>
        </section>

        <section class="press-kit-block" aria-labelledby="brand-assets-title">
          <h2 id="brand-assets-title">Logos and Icons</h2>
          <div class="press-asset-grid" data-logo-assets></div>
        </section>

        <section class="press-kit-block" aria-labelledby="promo-assets-title">
          <h2 id="promo-assets-title">Promotional Screenshots</h2>
          <div class="press-asset-grid" data-promo-assets></div>
        </section>

        <section class="press-kit-block" aria-labelledby="gameplay-assets-title">
          <h2 id="gameplay-assets-title">Gameplay Screenshots</h2>
          <div class="press-asset-grid" data-gameplay-assets></div>
        </section>

        <section class="press-kit-block" aria-labelledby="usage-title">
          <h2 id="usage-title">Brand Usage Notes</h2>
          <ul class="brand-notes">
            <li>Use actual gameplay screenshots before generic fantasy imagery.</li>
            <li>Keep the dark fantasy tone: charcoal, cave gray, maroon, aged gold, firelight.</li>
            <li>Use EvilEmpire for the Much Dungeon logo wordmark paired with the dungeon door icon.</li>
            <li>Avoid stretching, recoloring, or heavily filtering logos and icons.</li>
            <li>Use promotional screenshots when copy and logo treatment are helpful.</li>
            <li>Use raw gameplay screenshots when the article needs inspection or gameplay clarity.</li>
          </ul>
        </section>
      </div>
    </section>
  </main>

  <main class="legal-page" id="privacy-policy-page" data-page="privacy" hidden>
    <section class="privacy-section" aria-labelledby="privacy-title">
      <div class="section-shell legal-document">
        <a class="back-link" href="/" data-route-link>Back to Much Dungeon</a>
        <p class="eyebrow">Privacy</p>
        <h1 id="privacy-title">Privacy Policy</h1>
        <p class="legal-updated">Last Updated: June 26, 2026</p>
        <div data-privacy-grid></div>
      </div>
    </section>
  </main>

  <main class="legal-page" id="eula-page" data-page="eula" hidden>
    <section class="eula-section" aria-labelledby="eula-title">
      <div class="section-shell legal-document">
        <a class="back-link" href="/" data-route-link>Back to Much Dungeon</a>
        <p class="eyebrow">Legal</p>
        <h1 id="eula-title">End User License Agreement</h1>
        <p class="legal-updated">Effective Date: June 26, 2026</p>
        <div data-eula-grid></div>
      </div>
    </section>
  </main>

  <footer class="site-footer" id="links">
    <div class="section-shell footer-shell">
      <a class="footer-mark" href="#top" aria-label="Much Dungeon home">
        <img src="${brandIcon}" alt="" aria-hidden="true">
        <span>Much Dungeon</span>
      </a>
      <nav class="footer-links" aria-label="Footer links">
        <a href="https://muchdungeon.com">muchdungeon.com</a>
        <a href="https://muchsoftware.com">Much Software</a>
        <a href="https://twitter.com/MuchSoftware">X / Twitter</a>
        <a href="/press-kit" data-route-link>Press Kit</a>
        <a href="/blog-1" data-route-link>Blog</a>
        <a href="/release-notes" data-route-link>Release Notes</a>
        <a href="/privacy-policy" data-route-link>Privacy Policy</a>
        <a href="/eula" data-route-link>EULA</a>
      </nav>
    </div>
  </footer>

  <div class="lightbox" role="dialog" aria-modal="true" aria-labelledby="lightbox-caption" hidden data-lightbox>
    <button class="lightbox__backdrop" type="button" aria-label="Dismiss image preview" data-lightbox-close></button>
    <figure class="lightbox__panel">
      <button class="lightbox__close" type="button" aria-label="Close image preview" data-lightbox-close>Close</button>
      <img src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==" alt="" data-lightbox-image>
      <figcaption id="lightbox-caption" data-lightbox-caption></figcaption>
    </figure>
  </div>
`;

const featureGrid = document.querySelector<HTMLDivElement>("[data-feature-grid]");
const mediaStack = document.querySelector<HTMLDivElement>("[data-media-stack]");
const storeGrid = document.querySelector<HTMLDivElement>("[data-store-grid]");
const blogGrid = document.querySelector<HTMLDivElement>("[data-blog-grid]");
const postDetail = document.querySelector<HTMLDivElement>("[data-post-detail]");
const releaseList = document.querySelector<HTMLDivElement>("[data-release-list]");
const pressFactsList = document.querySelector<HTMLDListElement>("[data-press-facts]");
const logoAssetsGrid = document.querySelector<HTMLDivElement>("[data-logo-assets]");
const promoAssetsGrid = document.querySelector<HTMLDivElement>("[data-promo-assets]");
const gameplayAssetsGrid = document.querySelector<HTMLDivElement>("[data-gameplay-assets]");
const eulaGrid = document.querySelector<HTMLDivElement>("[data-eula-grid]");
const privacyGrid = document.querySelector<HTMLDivElement>("[data-privacy-grid]");
const menuButton = document.querySelector<HTMLButtonElement>("[data-menu-button]");
const siteNav = document.querySelector<HTMLElement>("[data-site-nav]");
const lightbox = document.querySelector<HTMLDivElement>("[data-lightbox]");
const lightboxImage = document.querySelector<HTMLImageElement>("[data-lightbox-image]");
const lightboxCaption = document.querySelector<HTMLElement>("[data-lightbox-caption]");

if (!featureGrid || !mediaStack || !storeGrid || !blogGrid || !postDetail || !releaseList || !pressFactsList || !logoAssetsGrid || !promoAssetsGrid || !gameplayAssetsGrid || !eulaGrid || !privacyGrid || !menuButton || !siteNav || !lightbox || !lightboxImage || !lightboxCaption) {
  throw new Error("Missing expected DOM nodes");
}

featureGrid.innerHTML = features.map((feature) => `
  <article class="feature-card">
    <button class="image-trigger" type="button" data-lightbox-src="${feature.image}" data-lightbox-alt="${feature.alt}" data-lightbox-caption="${feature.title}">
      <img src="${feature.image}" alt="${feature.alt}">
      <span class="view-full">View full image</span>
    </button>
    <div class="feature-card__body">
      <h3>${feature.title}</h3>
      <p>${feature.text}</p>
    </div>
  </article>
`).join("");

mediaStack.innerHTML = mediaItems.map((item) => `
  <article class="media-card">
    <button class="image-trigger" type="button" data-lightbox-src="${item.image}" data-lightbox-alt="${item.alt}" data-lightbox-caption="${item.title}">
      <img src="${item.image}" alt="${item.alt}">
      <span class="view-full">View full image</span>
    </button>
    <div>
      <h3>${item.title}</h3>
      <p>${item.text}</p>
    </div>
  </article>
`).join("");

storeGrid.innerHTML = storeLinks.map((store) => {
  const tag = store.url ? "a" : "div";
  const href = store.url ? ` href="${store.url}"` : "";
  const pendingClass = store.url ? "" : " is-pending";
  const badgeClass = store.badge ? " has-badge" : "";

  return `
    <${tag} class="store-card${pendingClass}${badgeClass}"${href} aria-label="${store.label}">
      ${store.badge
        ? `
          <img class="store-card__badge" src="${store.badge}" alt="${store.badgeAlt ?? store.label}">
        `
        : `
          <span class="store-card__icon" aria-hidden="true">${store.icon}</span>
          <span>
            <h3>${store.platform}</h3>
            <p>${store.label}</p>
            <p>${store.status}</p>
          </span>
        `}
    </${tag}>
  `;
}).join("");

pressFactsList.innerHTML = pressFacts.map(([term, description]) => `
  <div>
    <dt>${term}</dt>
    <dd>${description}</dd>
  </div>
`).join("");

const renderPressAssets = (assets: PressAsset[]) => assets.map((asset) => `
  <article class="press-asset-card">
    <a class="press-asset-card__image" href="${asset.image}" target="_blank" rel="noreferrer">
      <img src="${asset.image}" alt="${asset.alt}">
    </a>
    <div class="press-asset-card__body">
      <p class="content-kicker">${asset.fileLabel}</p>
      <h3>${asset.title}</h3>
      <p>${asset.description}</p>
      <div class="press-asset-actions">
        <a href="${asset.image}" target="_blank" rel="noreferrer">Open</a>
        <a href="${asset.image}" download>Download</a>
      </div>
    </div>
  </article>
`).join("");

logoAssetsGrid.innerHTML = renderPressAssets(pressLogoAssets);
promoAssetsGrid.innerHTML = renderPressAssets(pressPromoAssets);
gameplayAssetsGrid.innerHTML = renderPressAssets(pressGameplayAssets);

blogGrid.innerHTML = blogPosts.map((post) => `
  <article class="blog-card">
    <a class="blog-card__image" href="${post.path}" data-route-link>
      <img src="${post.cardImage}" alt="${post.cardImageAlt}">
    </a>
    <div class="content-meta">
      <time datetime="${post.date}">${post.date}</time>
      <span>${post.category}</span>
    </div>
    <h3><a href="${post.path}" data-route-link>${post.title}</a></h3>
    <p>${post.excerpt}</p>
    <p class="byline">${post.author}</p>
  </article>
`).join("");

const renderPostDetail = (post: BlogPost) => {
  postDetail.innerHTML = `
    <header class="post-header">
      <div class="content-meta">
        <time datetime="${post.date}">${post.date}</time>
        <span>${post.category}</span>
      </div>
      <h1>${post.title}</h1>
      <p class="post-author">${post.author}</p>
      <p class="post-excerpt">${post.excerpt}</p>
    </header>
    ${post.heroImage ? `
      <figure class="post-hero-media">
        <img src="${post.heroImage}" alt="${post.heroImageAlt ?? ""}">
      </figure>
    ` : ""}
    <div class="post-body">
      ${post.paragraphs.map((paragraph) => `<p>${paragraph}</p>`).join("")}
      ${post.bullets ? `<ul>${post.bullets.map((item) => `<li>${item}</li>`).join("")}</ul>` : ""}
    </div>
    ${post.media?.length ? `
      <div class="post-media-grid">
        ${post.media.map((item) => `
          <figure>
            <img src="${item.image}" alt="${item.alt}">
            <figcaption>
              <strong>${item.title}</strong>
              <span>${item.text}</span>
            </figcaption>
          </figure>
        `).join("")}
      </div>
    ` : ""}
  `;
};

releaseList.innerHTML = releaseNotes.map((release) => `
  <article class="release-card">
    <div class="release-card__header">
      <div>
        <p class="content-kicker">${release.status}</p>
        <h3>${release.version}</h3>
      </div>
      <time datetime="${release.date}">${release.date}</time>
    </div>
    <ul>
      ${release.notes.map((note) => `<li>${note}</li>`).join("")}
    </ul>
  </article>
`).join("");

const renderLegalSections = (sections: LegalSection[]) => sections.map((section) => `
  <section class="legal-text-section">
    <h2>${section.title}</h2>
    ${section.body.map((paragraph) => `<p>${paragraph.replaceAll("\n", "<br>")}</p>`).join("")}
    ${section.items ? `<ul>${section.items.map((item) => `<li>${item}</li>`).join("")}</ul>` : ""}
  </section>
`).join("");

eulaGrid.innerHTML = renderLegalSections(eulaSections);
privacyGrid.innerHTML = renderLegalSections(privacySections);

const normalizePath = (path: string) => path.replace(/\/$/, "") || "/";

const routeForPath = (path: string): RouteTarget => {
  const normalizedPath = normalizePath(path);

  if (normalizedPath.startsWith("/post/")) {
    const post = blogPosts.find((candidate) => normalizePath(candidate.path) === normalizedPath);

    if (post) {
      return { sectionId: "blog-post-page", title: `${post.title} | Much Dungeon`, page: "blog-post", post };
    }

    return { sectionId: "blog", title: "Blog | Much Dungeon", page: "home" };
  }

  return routeTargets[normalizedPath] ?? routeTargets["/"];
};

const absoluteUrl = (path: string) => `${siteOrigin}${path === "/" ? "/" : path}`;

const upsertMeta = (selector: string, attribute: "content" | "href", value: string, create: () => HTMLElement) => {
  const existing = document.head.querySelector<HTMLElement>(selector);
  const element = existing ?? create();

  element.setAttribute(attribute, value);

  if (!existing) {
    document.head.append(element);
  }
};

const setNamedMeta = (name: string, content: string) => {
  upsertMeta(`meta[name="${name}"]`, "content", content, () => {
    const meta = document.createElement("meta");
    meta.setAttribute("name", name);
    return meta;
  });
};

const setPropertyMeta = (property: string, content: string) => {
  upsertMeta(`meta[property="${property}"]`, "content", content, () => {
    const meta = document.createElement("meta");
    meta.setAttribute("property", property);
    return meta;
  });
};

const setCanonical = (href: string) => {
  upsertMeta('link[rel="canonical"]', "href", href, () => {
    const link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    return link;
  });
};

const setRouteStructuredData = (route: RouteTarget, meta: SeoMeta, canonicalUrl: string) => {
  const id = "route-structured-data";
  document.getElementById(id)?.remove();

  if (!route.post) {
    return;
  }

  const script = document.createElement("script");
  script.id = id;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": route.post.title,
    "description": meta.description,
    "datePublished": route.post.date,
    "dateModified": route.post.date,
    "author": {
      "@type": "Organization",
      "name": route.post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Much Software, LLC",
      "url": "https://muchsoftware.com"
    },
    "mainEntityOfPage": canonicalUrl,
    "image": route.post.heroImage ? socialImageUrl : socialImageUrl
  });
  document.head.append(script);
};

const metaForRoute = (path: string, route: RouteTarget): SeoMeta => {
  if (route.post) {
    return {
      title: `${route.post.title} | Much Dungeon`,
      description: route.post.excerpt,
      canonicalPath: route.post.path,
      type: "article"
    };
  }

  const normalizedPath = normalizePath(path);
  return routeMeta[normalizedPath] ?? routeMeta["/"];
};

const updateSeo = (path: string, route: RouteTarget) => {
  const meta = metaForRoute(path, route);
  const canonicalUrl = absoluteUrl(meta.canonicalPath);

  document.title = meta.title;
  setCanonical(canonicalUrl);
  setNamedMeta("description", meta.description);
  setNamedMeta("twitter:card", "summary_large_image");
  setNamedMeta("twitter:title", meta.title);
  setNamedMeta("twitter:description", meta.description);
  setNamedMeta("twitter:image", socialImageUrl);
  setPropertyMeta("og:title", meta.title);
  setPropertyMeta("og:description", meta.description);
  setPropertyMeta("og:type", meta.type ?? "website");
  setPropertyMeta("og:url", canonicalUrl);
  setPropertyMeta("og:image", socialImageUrl);
  setPropertyMeta("og:image:width", "1200");
  setPropertyMeta("og:image:height", "630");
  setPropertyMeta("og:site_name", "Much Dungeon");
  setRouteStructuredData(route, meta, canonicalUrl);
};

const scrollToRoute = (path: string, behavior: ScrollBehavior = "smooth") => {
  const route = routeForPath(path);
  updateSeo(path, route);
  document.querySelectorAll<HTMLElement>("[data-page]").forEach((page) => {
    page.hidden = page.dataset.page !== route.page;
  });
  if (route.post) {
    renderPostDetail(route.post);
  }
  document.getElementById(route.sectionId)?.scrollIntoView({ behavior, block: "start" });
};

menuButton.addEventListener("click", () => {
  const isOpen = siteNav.classList.toggle("is-open");
  document.body.classList.toggle("menu-open", isOpen);
  menuButton.setAttribute("aria-expanded", String(isOpen));
});

siteNav.addEventListener("click", (event) => {
  if (event.target instanceof HTMLAnchorElement) {
    siteNav.classList.remove("is-open");
    document.body.classList.remove("menu-open");
    menuButton.setAttribute("aria-expanded", "false");
  }
});

document.addEventListener("click", (event) => {
  if (!(event.target instanceof Element)) {
    return;
  }

  const routeLink = event.target.closest<HTMLAnchorElement>("[data-route-link]");
  if (!routeLink) {
    return;
  }

  const href = routeLink.getAttribute("href");
  if (!href || href.startsWith("http") || href.startsWith("#")) {
    return;
  }

  event.preventDefault();
  window.history.pushState({}, "", href);
  scrollToRoute(href);
});

window.addEventListener("popstate", () => {
  scrollToRoute(window.location.pathname);
});

requestAnimationFrame(() => {
  scrollToRoute(window.location.pathname, "auto");
});

const openLightbox = (trigger: HTMLButtonElement) => {
  lightboxImage.src = trigger.dataset.lightboxSrc ?? "";
  lightboxImage.alt = trigger.dataset.lightboxAlt ?? "";
  lightboxCaption.textContent = trigger.dataset.lightboxCaption ?? "";
  lightbox.hidden = false;
  document.body.classList.add("modal-open");
  lightbox.querySelector<HTMLButtonElement>("[data-lightbox-close]")?.focus();
};

const closeLightbox = () => {
  lightbox.hidden = true;
  document.body.classList.remove("modal-open");
  lightboxImage.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";
  lightboxImage.alt = "";
  lightboxCaption.textContent = "";

  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

document.addEventListener("click", (event) => {
  if (!(event.target instanceof Element)) {
    return;
  }

  if (event.target.matches("[data-lightbox-close]")) {
    event.preventDefault();
    event.stopPropagation();
    closeLightbox();
    return;
  }

  const trigger = event.target.closest<HTMLButtonElement>("[data-lightbox-src]");
  if (trigger instanceof HTMLButtonElement) {
    openLightbox(trigger);
  }
});

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && !lightbox.hidden) {
    closeLightbox();
  }
});
